import type { OrderItemRow } from '@ims/data'
import { Button, Field, Input, Modal, Select, cx, useToast } from '@ims/ui'
import { CookingPot, Ban, Wine } from '@ims/ui/icons'
import { useState } from 'react'
import { MoneyInput, QtyInput } from './components.tsx'
import { useLive, useSettings } from './hooks.ts'
import { finePointer } from './lib/pointer.ts'
import { useRepo } from './workspace.tsx'

// A line typed by hand, without a product: a repair charge, a one-off item, "Goods" on
// quick bill (docs/plans/custom-items.md), or a dish that isn't on the menu on a table's
// order, which also says where its ticket goes (docs/plans/custom-order-items.md). It
// never touches stock; a manager can say later which products it was (invoice page →
// Assign products). Who may add one is `tenant_settings.custom_items`; the server checks it.

export type Station = OrderItemRow['station']

export interface CustomItem {
  description: string
  /** For one. */
  unitPricePaisa: number
  qtyMilli: number
  priceIncludesTax: boolean
  taxRateId: string | null
  taxRateBp: number
  /** Where a table's order sends it (quick bill has no kitchen). */
  station: Station
}

/** Who may add a custom item, by the business's setting and the person's role. */
export function canAddCustom(
  rule: 'everyone' | 'managers' | 'off' | undefined,
  isManager: boolean,
): boolean {
  const r = rule ?? 'everyone'
  return r === 'everyone' || (r === 'managers' && isManager)
}

const STATIONS: { value: Station; label: string; icon: typeof CookingPot }[] = [
  { value: 'kitchen', label: 'Kitchen', icon: CookingPot },
  { value: 'bar', label: 'Bar', icon: Wine },
  { value: 'none', label: 'No ticket', icon: Ban },
]

export function CustomItemDialog({
  onClose,
  onAdd,
  addLabel = 'Add to bill',
  order = false,
}: {
  onClose: () => void
  onAdd: (item: CustomItem) => void
  addLabel?: string
  /** On a table's order: a dish's words and where its ticket goes. */
  order?: boolean
}) {
  const repo = useRepo()
  const settings = useSettings()
  const toast = useToast()
  const rates = useLive(
    (r) => r.list('tax_rates', { orderBy: { column: 'rate_bp', direction: 'desc' }, limit: 100 }),
    [],
    ['tax_rates'],
  ).data?.rows
  const [description, setDescription] = useState('')
  const [amount, setAmount] = useState<number | null>(null)
  const [qtyMilli, setQtyMilli] = useState<number | null>(1000)
  const [taxChoice, setTaxChoice] = useState<string | null>(null)
  const [station, setStation] = useState<Station>('kitchen')
  const includesTax = settings?.prices_include_tax ?? true
  const taxId = taxChoice ?? settings?.default_tax_rate_id ?? ''

  const add = async () => {
    if (!description.trim()) return toast.error('Describe the item')
    if (amount === null || amount <= 0) return toast.error('Enter the amount')
    if (qtyMilli === null || qtyMilli <= 0) return toast.error('Enter the quantity')
    // The rate is read now, like a scanned product's (lists may still be loading).
    const tax = taxId ? await repo.get('tax_rates', taxId) : null
    onAdd({
      description: description.trim(),
      unitPricePaisa: amount,
      qtyMilli,
      priceIncludesTax: includesTax,
      taxRateId: tax?.id ?? null,
      taxRateBp: tax?.rate_bp ?? 0,
      station: order ? station : 'none',
    })
  }

  return (
    <Modal
      open
      onClose={onClose}
      title="Custom item"
      size="sm"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button onClick={() => void add()}>{addLabel}</Button>
        </>
      }
    >
      <form
        className="space-y-3"
        onSubmit={(e) => {
          e.preventDefault()
          void add()
        }}
      >
        <Field
          label="Description"
          hint={
            order
              ? 'Printed on the kitchen ticket and the bill. Stock does not change.'
              : 'Printed on the bill. Stock does not change.'
          }
        >
          {(id) => (
            <Input
              id={id}
              autoFocus={finePointer}
              maxLength={200}
              placeholder={order ? 'e.g. Special thali, less oil' : 'e.g. Repair charge'}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          )}
        </Field>
        <div className="grid grid-cols-[1fr_6rem] gap-3">
          <Field
            label="Amount"
            hint={includesTax ? 'For one, VAT included' : 'For one, before VAT'}
          >
            {(id) => (
              <MoneyInput
                id={id}
                prefix={settings?.currency_symbol}
                placeholder="0.00"
                paisa={amount}
                onValue={setAmount}
              />
            )}
          </Field>
          <Field label="Quantity">
            {(id) => <QtyInput id={id} milli={qtyMilli} onValue={setQtyMilli} />}
          </Field>
        </div>
        <Field label="VAT">
          {(id) => (
            <Select id={id} value={taxId} onChange={(e) => setTaxChoice(e.target.value)}>
              <option value="">No VAT (exempt)</option>
              {(rates ?? [])
                .filter((t) => t.is_active || t.id === taxId)
                .map((t) => (
                  <option key={t.id} value={t.id}>
                    {t.name}
                  </option>
                ))}
            </Select>
          )}
        </Field>
        {order && (
          <div>
            <p id="custom-goes-to" className="mb-1.5 text-sm font-semibold">
              Goes to
            </p>
            <div
              role="radiogroup"
              aria-labelledby="custom-goes-to"
              className="grid grid-cols-3 gap-1 rounded-xl bg-soft p-1"
            >
              {STATIONS.map((s) => (
                <button
                  key={s.value}
                  type="button"
                  role="radio"
                  aria-checked={station === s.value}
                  onClick={() => setStation(s.value)}
                  className={cx(
                    'flex h-10 items-center justify-center gap-1.5 rounded-lg text-sm whitespace-nowrap pos:h-12',
                    station === s.value
                      ? 'bg-surface font-bold text-ink shadow-card ring-1 ring-border'
                      : 'font-semibold text-muted hover:text-ink',
                  )}
                >
                  <s.icon className="size-4" aria-hidden />
                  {s.label}
                </button>
              ))}
            </div>
            <p className="mt-1.5 text-xs text-muted">
              No ticket: a charge the kitchen needn’t see, such as corkage.
            </p>
          </div>
        )}
        <button type="submit" hidden />
      </form>
    </Modal>
  )
}
